import ApplicationController from './application_controller'

const NUDGE_STEP = 0.005
const skipSensorState = {} // resets on full page reload; survives StimulusReflex panel morphs
const clamp = v => Math.min(Math.max(v, 0), 1)

export default class extends ApplicationController {
  static targets = ['sensorRow', 'comment', 'verdictButton', 'addSensorsCheckbox', 'productSelect', 'productIcon']

  connect() {
    if (!this.hasAddSensorsCheckboxTarget) return
    if (skipSensorState[this.element.dataset.proposedPlacementId] === true) {
      this.addSensorsCheckboxTarget.checked = false
      this._applySkipSensor(true)
    }
  }

  toggleAddSensors() {
    const skip = !this.addSensorsCheckboxTarget.checked
    skipSensorState[this.element.dataset.proposedPlacementId] = skip
    this._applySkipSensor(skip)
  }

  _applySkipSensor(skip) {
    this.verdictButtonTargets.forEach(btn => { btn.dataset.skipSensor = skip ? 'true' : 'false' })
  }

  // Staff picked a concrete product from the group's select: update the adjacent icon and propagate the
  // chosen product id onto this row's verdict buttons (so the reflex places exactly that product).
  changeProduct(event) {
    const select = event.currentTarget
    const option = select.options[select.selectedIndex]
    const row = select.closest('.proposed-sensor')
    if (!row) return

    const icon = row.querySelector('[data-target~="proposed-placement.productIcon"]')
    if (icon && option) {
      icon.style.backgroundImage = `url(${option.dataset.shapeUrl})`
      icon.style.color = option.dataset.color
    }

    row.querySelectorAll('[data-target~="proposed-placement.verdictButton"]')
      .forEach(btn => { btn.dataset.productId = select.value })
  }

  highlight(event) {
    const row = event.currentTarget
    this.sensorRowTargets.forEach(r => r.classList.remove('selected'))
    row.classList.add('selected')

    const posX = row.dataset.posX
    const posY = row.dataset.posY
    document.querySelectorAll('.proposed-sensor-marker.highlighted, .point.highlighted').forEach(m => m.classList.remove('highlighted'))

    const placementId = row.dataset.placementId
    if (placementId) {
      document.getElementById(`point-${placementId}`)?.classList.add('highlighted')
    } else {
      const markerId = `proposed-marker-${posX}-${posY}`.replace(/\./g, '_')
      document.getElementById(markerId)?.classList.add('highlighted')
    }
  }

  showMoveControls(event) {
    const row = event.currentTarget.closest('[data-target~="proposed-placement.sensorRow"]')
    if (!row) return
    event.currentTarget.style.display = 'none'
    row.querySelector('.move-controls').classList.remove('hidden')
  }

  nudge(event) {
    const btn = event.currentTarget
    const direction = btn.dataset.direction
    const posX = btn.dataset.suggestedPosX
    const posY = btn.dataset.suggestedPosY
    const markerId = `proposed-marker-${posX}-${posY}`.replace(/\./g, '_')
    const marker = document.getElementById(markerId)
    if (!marker) return

    const currentX = parseFloat(marker.dataset.correctedPosX ?? posX)
    const currentY = parseFloat(marker.dataset.correctedPosY ?? posY)

    const newX = direction === 'left' ? clamp(currentX - NUDGE_STEP) : direction === 'right' ? clamp(currentX + NUDGE_STEP) : currentX
    const newY = direction === 'up'   ? clamp(currentY - NUDGE_STEP) : direction === 'down'  ? clamp(currentY + NUDGE_STEP) : currentY

    marker.style.left = `calc(${newX * 100}% - 11px)`
    marker.style.top  = `calc(${newY * 100}% - 11px)`
    marker.dataset.correctedPosX = newX.toFixed(6)
    marker.dataset.correctedPosY = newY.toFixed(6)

    this._syncOkButton(posX, posY, newX, newY)
  }

  restoreMarker(event) {
    const btn = event.currentTarget
    const posX = btn.dataset.suggestedPosX
    const posY = btn.dataset.suggestedPosY
    const markerId = `proposed-marker-${posX}-${posY}`.replace(/\./g, '_')
    const marker = document.getElementById(markerId)
    if (!marker) return

    marker.style.left = `calc(${parseFloat(posX) * 100}% - 11px)`
    marker.style.top  = `calc(${parseFloat(posY) * 100}% - 11px)`
    delete marker.dataset.correctedPosX
    delete marker.dataset.correctedPosY

    this._syncOkButton(posX, posY, parseFloat(posX), parseFloat(posY))
  }

  confirmAdd(event) {
    const button = event.currentTarget
    const row = button.closest('.proposed-sensor')
    const commentField = row?.querySelector('[data-target~="proposed-placement.comment"]')
    button.dataset.comment = commentField ? commentField.value.trim() : ''
  }

  submitVerdict(event) {
    const button = event.currentTarget
    const requiresComment = button.dataset.requiresComment === 'true'
    const requiresPosition = button.dataset.requiresPosition === 'true'

    const row = button.closest('[data-target~="proposed-placement.sensorRow"]') ||
                button.closest('.proposed-sensor')
    const commentField = row ? row.querySelector('[data-target~="proposed-placement.comment"]') : null
    const comment = commentField ? commentField.value.trim() : ''

    if (requiresComment && !comment) {
      if (commentField) {
        commentField.classList.add('comment-error')
        commentField.focus()
        commentField.addEventListener('input', () => commentField.classList.remove('comment-error'), { once: true })
      }
      event.preventDefault()
      event.stopImmediatePropagation()
      return
    }

    button.dataset.comment = comment

    const posX = button.dataset.suggestedPosX
    const posY = button.dataset.suggestedPosY
    const verdict = button.dataset.verdict
    const markerId = `proposed-marker-${posX}-${posY}`.replace(/\./g, '_')
    const marker = document.getElementById(markerId)

    if (requiresPosition) {
      button.dataset.correctedPosX = (marker?.dataset.correctedPosX) || posX
      button.dataset.correctedPosY = (marker?.dataset.correctedPosY) || posY
    }

    document.querySelectorAll('.proposed-sensor-marker.highlighted, .point.highlighted').forEach(m => m.classList.remove('highlighted'))
    this.sensorRowTargets.forEach(r => r.classList.remove('selected'))

    if (marker) {
      marker.classList.remove('verdict-ok', 'verdict-move', 'verdict-decline')
      marker.classList.add(`verdict-${verdict}`)

      if (verdict === 'move') {
        marker.style.left = `calc(${parseFloat(posX) * 100}% - 11px)`
        marker.style.top  = `calc(${parseFloat(posY) * 100}% - 11px)`
        delete marker.dataset.correctedPosX
        delete marker.dataset.correctedPosY
        const cx = parseFloat(button.dataset.correctedPosX)
        const cy = parseFloat(button.dataset.correctedPosY)
        if (!isNaN(cx) && !isNaN(cy)) this._drawMoveLine(posX, posY, cx, cy, markerId)
      } else if (verdict === 'decline') {
        marker.style.left = `calc(${parseFloat(posX) * 100}% - 11px)`
        marker.style.top  = `calc(${parseFloat(posY) * 100}% - 11px)`
        delete marker.dataset.correctedPosX
        delete marker.dataset.correctedPosY
      }
    }
  }

  _drawMoveLine(posX, posY, correctedX, correctedY, markerId) {
    const lineId = `proposed-line-${markerId.replace('proposed-marker-', '')}`
    const svg = this.element.closest('[id^="floorplan-"]')?.querySelector('.proposed-move-lines')
    if (!svg) return
    document.getElementById(lineId)?.remove()
    const line = document.createElementNS('http://www.w3.org/2000/svg', 'line')
    line.id = lineId
    line.setAttribute('x1', `${parseFloat(posX) * 100}%`)
    line.setAttribute('y1', `${parseFloat(posY) * 100}%`)
    line.setAttribute('x2', `${correctedX * 100}%`)
    line.setAttribute('y2', `${correctedY * 100}%`)
    line.setAttribute('stroke', '#7a5c00')
    line.setAttribute('stroke-width', '1.5')
    line.setAttribute('stroke-dasharray', '4,2')
    svg.appendChild(line)
  }

  _syncOkButton(posX, posY, currentX, currentY) {
    const row = this.sensorRowTargets.find(r => r.dataset.posX === posX && r.dataset.posY === posY)
    if (!row) return
    const btn = row.querySelector('.btn-ok, .btn-ok-move')
    if (!btn) return

    const moved = Math.abs(currentX - parseFloat(posX)) > 1e-6 || Math.abs(currentY - parseFloat(posY)) > 1e-6

    if (moved) {
      btn.classList.replace('btn-ok', 'btn-ok-move')
      btn.dataset.verdict = 'move'
      btn.dataset.requiresPosition = 'true'
      btn.dataset.requiresComment = 'true'
      btn.textContent = `↔ ${btn.dataset.okLabel}`
    } else {
      btn.classList.replace('btn-ok-move', 'btn-ok')
      btn.dataset.verdict = 'ok'
      delete btn.dataset.requiresPosition
      delete btn.dataset.requiresComment
      btn.textContent = `✓ ${btn.dataset.okLabel}`
    }
  }
}
